import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Wrapper para carruseles horizontales que muestra dos pills semi-
// transparentes (back / forward) al hacer hover sobre el área del
// carrusel — solo en desktop (`md+`). Los pills disparan un scroll
// suave del ~80% del ancho visible y se ocultan automáticamente cuando
// no hay más para scrollear en esa dirección.
//
// Reemplaza directamente al `<div className="flex overflow-x-auto ...">`
// que envuelve los items: se le pasan las mismas `className` / `style`
// que tendría ese div. La animación CSS (`animate-mode-swap`) sigue
// funcionando porque el div interno se remonta junto con el wrapper
// cuando el `key` del padre cambia.

interface HoverScrollCarouselProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  /** Offset en píxeles entre el pill y el borde del wrapper. Más chico
   *  = más pegado al borde. Default: 6. */
  arrowInset?: number;
  /** Handler de scroll del div interno — útil para que el caller siga la
   *  posición (p. ej. dots de un carrusel de imágenes). */
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  /** Ref al div scrolleable interno — para que el caller pueda hacer
   *  `scrollTo` (p. ej. al tocar un dot). */
  innerRef?: React.MutableRefObject<HTMLDivElement | null>;
  /** Clases extra del wrapper (el `relative` que contiene las flechas). Para cuando el
   *  carrusel tiene que tomar su ALTO de un padre flex (`min-h-0 flex-1`): el wrapper no
   *  tiene alto propio y sin esto el scroller de adentro no tiene contra qué medirse. */
  wrapperClassName?: string;
  /** Si true, los pills se centran verticalmente respecto al primer
   *  elemento `[data-carousel-media]` (ej. el contenedor de video/imagen
   *  de la card) en vez de respecto a TODO el alto del carrusel (que
   *  incluiría título + descripción debajo). Se mide el centro real del
   *  media y se recalcula en resize, así funciona con aspect-ratios
   *  responsivos. Default: false (centra al 50% del wrapper). */
  arrowAlignToMedia?: boolean;
}

export const HoverScrollCarousel: React.FC<HoverScrollCarouselProps> = ({
  className,
  style,
  children,
  arrowInset = 6,
  onScroll,
  innerRef,
  arrowAlignToMedia = false,
  wrapperClassName = '',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Callback ref que asigna el nodo tanto al ref interno como al `innerRef`
  // opcional del caller.
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (innerRef) innerRef.current = node;
    },
    [innerRef],
  );
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  // Posición vertical (px desde el top del wrapper) del centro de los
  // pills cuando `arrowAlignToMedia`. null = usar el default `top-1/2`.
  const [arrowTop, setArrowTop] = useState<number | null>(null);

  const measureArrowTop = useCallback(() => {
    if (!arrowAlignToMedia) { setArrowTop(null); return; }
    const scroller = scrollRef.current;
    const wrapper = wrapperRef.current;
    if (!scroller || !wrapper) return;
    const media = scroller.querySelector<HTMLElement>('[data-carousel-media]');
    if (!media) { setArrowTop(null); return; }
    const mRect = media.getBoundingClientRect();
    const wRect = wrapper.getBoundingClientRect();
    setArrowTop(mRect.top - wRect.top + mRect.height / 2);
  }, [arrowAlignToMedia]);

  // Recalculamos los affordances (¿hay más para scrollear?) en cada
  // scroll, resize del container y cambio de children. 4px de tolerancia
  // para evitar parpadeo del pill cuando el browser deja un sub-pixel
  // residual.
  const updateAffordance = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateAffordance();
    measureArrowTop();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      updateAffordance();
      measureArrowTop();
    });
    ro.observe(el);
    // Observamos también el media: su alto cambia con el aspect-ratio al
    // redimensionar la card aunque el alto del scroller no varíe.
    const media = el.querySelector('[data-carousel-media]');
    if (media) ro.observe(media);
    window.addEventListener('resize', measureArrowTop);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureArrowTop);
    };
  }, [updateAffordance, measureArrowTop, children]);

  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  // `transition-opacity` (no `transition-all`) + clase neutra de animación
  // para que los pills NO hereden el `animate-mode-swap` (translate+fade)
  // del scroll container cuando éste lo aplica. Sin esto, los pills
  // "se deslizan hacia abajo" al cliquearlos porque la animación CSS se
  // re-aplica al remount del wrapper.
  //
  // El `group-hover/hsc:opacity-100` se aplica condicionalmente desde
  // cada botón (solo cuando hay scroll en esa dirección), porque su
  // specificity gana al `disabled:opacity-0` y el pill se "asoma" en
  // hover aunque esté disabled.
  const arrowBase =
    'hidden md:inline-flex absolute top-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-black/45 hover:bg-black/65 text-white backdrop-blur-md shadow-md opacity-0 transition-opacity duration-200 focus:outline-none';
  // `transform` aparte del Tailwind para que no entre en conflicto con
  // animaciones de translate que pueda heredar el wrapper. Lo dejamos
  // hard-coded por inline style.
  const arrowStyle: React.CSSProperties = { transform: 'translateY(-50%)' };

  return (
    // `w-full min-w-0` evita que el wrapper se shrink-to-fit en contextos
    // flex (ej. el sheet de ModelPicker, que pone el carousel adentro de
    // un `flex items-center`). Sin esto el wrapper relative quedaba con
    // ancho 0 y el inner scroll div no podía scrollear horizontalmente.
    <div ref={wrapperRef} className={`group/hsc relative w-full min-w-0 ${wrapperClassName}`}>
      <div
        ref={setRefs}
        className={className}
        style={style}
        onScroll={(e) => {
          updateAffordance();
          onScroll?.(e);
        }}
      >
        {children}
      </div>
      <button
        type="button"
        aria-label="Anterior"
        // `onMouseDown.preventDefault` evita que el botón tome focus al
        // click — sin focus el browser no intenta hacer scroll-into-view
        // y el pill no "salta".
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => scrollByDir(-1)}
        disabled={!canPrev}
        aria-hidden={!canPrev}
        style={{ ...arrowStyle, left: arrowInset, ...(arrowTop != null ? { top: arrowTop } : {}) }}
        className={`${arrowBase} ${canPrev ? 'group-hover/hsc:opacity-100' : 'pointer-events-none'}`}
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        type="button"
        aria-label="Siguiente"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => scrollByDir(1)}
        disabled={!canNext}
        aria-hidden={!canNext}
        style={{ ...arrowStyle, right: arrowInset, ...(arrowTop != null ? { top: arrowTop } : {}) }}
        className={`${arrowBase} ${canNext ? 'group-hover/hsc:opacity-100' : 'pointer-events-none'}`}
      >
        <ChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
};
